import React, { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { Button, Box, CircularProgress } from '@mui/material';

import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import { SnackbarSeverityType } from '../../../enums';
import { StyledButton } from '../styled';
import { ScoringType, GradeScale, Subject } from '../types';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import pallet from '../../../layouts/pallet';

interface AddSubjectModalProps {
  open: boolean;
  onClose: () => void;
  onAddSubject: (subjectData: {
    subject: Subject;
    scoringType: ScoringType;
    gradeScale?: GradeScale;
    maxMarks?: number;
  }) => void;
  subjectOptions: Subject[];
  gradeScaleOptions: GradeScale[];
  school?: string;
  scoringTypeOptions: ScoringType[];
  isLoadingScoringTypes: boolean;
  isLoadingGradeScales: boolean;
  isLoadingSubjects: boolean;
  existingSubjects: Subject[];
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}

const AddSubjectModal: React.FC<AddSubjectModalProps> = ({
  open,
  onClose,
  onAddSubject,
  subjectOptions,
  gradeScaleOptions,
  school,
  scoringTypeOptions,
  isLoadingScoringTypes,
  isLoadingGradeScales,
  isLoadingSubjects,
  existingSubjects,
  showToast
}) => {
  const [subject, setSubject] = useState('');
  const [scoringType, setScoringType] = useState('');
  const [gradeScale, setGradeScale] = useState('');
  const [maxMarks, setMaxMarks] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject('');
      setScoringType('');
      setGradeScale('');
      setMaxMarks('');
      setIsAdding(false);
    }
  }, [open]);

  const handleAdd = () => {
    setIsAdding(true);
    const selectedSubject = subjectOptions.find((opt) => opt.id === subject);
    const selectedScoringType = scoringTypeOptions.find(
      (opt) => opt.id === scoringType
    );
    const selectedGradeScale = gradeScaleOptions.find(
      (opt) => opt.id === gradeScale
    );

    if (!selectedSubject || !selectedScoringType) {
      showToast('Please select required fields.', SnackbarSeverityType.error);
      setIsAdding(false);
      return;
    }

    const numericMax = isEmpty(maxMarks) ? undefined : parseInt(maxMarks, 10);
    onAddSubject({
      subject: selectedSubject,
      scoringType: selectedScoringType,
      ...(selectedGradeScale ? { gradeScale: selectedGradeScale } : {}),
      ...(needsMaxMarks && numericMax !== undefined
        ? { maxMarks: numericMax }
        : {})
    });
    setSubject('');
    setScoringType('');
    setGradeScale('');
    setMaxMarks('');
    onClose();
    setIsAdding(false);
  };

  const selectedScoringType = scoringTypeOptions.find(
    (st) => st.id === scoringType
  );
  const needsMaxMarks =
    !!scoringType &&
    (!selectedScoringType?.validScores ||
      selectedScoringType.validScores.length === 0);

  useEffect(() => {
    if (!needsMaxMarks) {
      setMaxMarks('');
    }
  }, [needsMaxMarks]);
  const isAddButtonDisabled =
    !subject || !scoringType || (needsMaxMarks && !maxMarks) || isAdding;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Add Subject in Exam'
      actions={
        <>
          <Button
            onClick={onClose}
            color='primary'
            sx={{ border: `1px solid ${pallet.primary}` }}
          >
            Cancel
          </Button>
          <StyledButton onClick={handleAdd} disabled={isAddButtonDisabled}>
            {isAdding ? <CircularProgress size={24} color='inherit' /> : 'Add'}
          </StyledButton>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DropdownSelect
          label='Subject'
          placeholder='Select Subject'
          name='subject'
          value={subject}
          options={subjectOptions
            .filter((sub) => !existingSubjects.some((es) => es.id === sub.id))
            .map((sub) => ({
              value: sub.id || '',
              label: sub.name
            }))}
          onChange={(e) => setSubject(e.target.value as string)}
          fullWidth
          loading={isLoadingSubjects}
        />
        <DropdownSelect
          label='Scoring Type'
          placeholder='Select Scoring Type'
          name='scoringType'
          value={scoringType}
          options={scoringTypeOptions.map((st) => ({
            value: st.id || '',
            label: st.name
          }))}
          onChange={(e) => setScoringType(e.target.value as string)}
          required
          fullWidth
          loading={isLoadingScoringTypes}
        />
        {needsMaxMarks && (
          <TextInputWithIcon
            label='Max Marks'
            placeholder='Enter Max Marks'
            name='maxMarks'
            value={maxMarks}
            onChange={(e) => setMaxMarks(e.target.value)}
            fullWidth
            type='number'
            required
          />
        )}
        <DropdownSelect
          label='Grade Scale'
          placeholder='Select Grade Scale'
          name='gradeScale'
          value={gradeScale}
          options={gradeScaleOptions.map((gs) => ({
            value: gs.id || '',
            label: gs.name
          }))}
          onChange={(e) => setGradeScale(e.target.value as string)}
          fullWidth
          loading={isLoadingGradeScales}
        />
      </Box>
    </DialogModal>
  );
};

export default AddSubjectModal;
